import * as React from 'react';

import { AnalyticsStore } from 'bernie-core';

import { StaticMultiItemCrossSell } from '@shared-ui/multi-item-static-cross-sell';

import { adaptSearchCriteria } from 'src/common/stores/adapters/bex-api/common/search-criteria-adapter';
import { usePlatformContext } from 'src/common/stores/contexts/platformContext';
import { useSearchCriteriaContext } from 'stores/contexts';

import { StaticLodgingMultiItemCrossSellViewModel } from 'src/common/models';

import { ResultProps } from 'components/search/result';
import { StaticMultiItemCrossSellFallback } from './static-lodging-multi-item-cross-sell-fallback';

export interface StaticMultiItemCrossSellWrapperComponentProps extends ResultProps {
  model: StaticLodgingMultiItemCrossSellViewModel;
  crossSell: typeof StaticMultiItemCrossSell;
}

const analyticsCallback = (analytics?: AnalyticsStore) => (rfrr: string, linkName: string) =>
  analytics.trackEvent(rfrr, linkName, null, null, true);

export const StaticMultiItemCrossSellWrapperComponent: React.FC<StaticMultiItemCrossSellWrapperComponentProps> = React.memo(
  ({ model: { crossSellProductType, fallbackListingViewModel }, className, crossSell, analytics, ...rest }) => {
    const context = usePlatformContext();
    const searchCriteria = useSearchCriteriaContext();

    const propertySearchCriteria = adaptSearchCriteria(context, searchCriteria, false);

    const memoizedAnalyticsCallback = React.useCallback(analyticsCallback(analytics), [analytics]);
    const memoizedNoResultFallback = React.useCallback(
      () => (
        <StaticMultiItemCrossSellFallback
          {...rest}
          className={className ? className.replace('listing', '') : className}
          analytics={analytics}
          fallbackListingViewModel={fallbackListingViewModel}
        />
      ),
      [className, analytics, fallbackListingViewModel, rest]
    );

    return crossSellProductType ? (
      <li className={className}>
        {React.createElement(crossSell, {
          skipSsr: true,
          inputs: {
            crossSellProductType,
            searchContext: { properties: [propertySearchCriteria] },
          },
          analyticsCallback: memoizedAnalyticsCallback,
          noResultsFallback: memoizedNoResultFallback,
        })}
      </li>
    ) : null;
  }
);
StaticMultiItemCrossSellWrapperComponent.displayName = 'StaticMultiItemCrossSellWrapperComponent';

export interface StaticMultiItemCrossSellWrapperProps extends ResultProps {
  model: StaticLodgingMultiItemCrossSellViewModel;
}

export const StaticMultiItemCrossSellWrapper: React.FC<StaticMultiItemCrossSellWrapperProps> = props => (
  <StaticMultiItemCrossSellWrapperComponent {...props} crossSell={StaticMultiItemCrossSell} />
);
StaticMultiItemCrossSellWrapper.displayName = 'StaticMultiItemCrossSellWrapper';
